import {
  animate,
  useDragControls,
  useMotionValue,
  type PanInfo,
} from "motion/react";

export type DismissEdge = "bottom" | "left" | "right";

export type DragToDismissOptions = {
  edge: DismissEdge;
  enabled: boolean;
  onDismiss: () => void;
  isElastic?: boolean;
  isDirectionLocked?: boolean;
};

// A drag must travel at least this far (or be flicked at least this fast)
// towards the edge to dismiss the surface
const dismissDistancePx = 80;
const dismissFlickVelocityPxPerSecond = 500;

const settleSpring = { type: "spring", stiffness: 300, damping: 30 } as const;

const edges = {
  bottom: { axis: "y", constraints: { top: 0 }, direction: 1 },
  left: { axis: "x", constraints: { right: 0 }, direction: -1 },
  right: { axis: "x", constraints: { left: 0 }, direction: 1 },
} as const;

export const useDragToDismiss = ({
  edge,
  enabled,
  onDismiss,
  isElastic = true,
  isDirectionLocked = false,
}: DragToDismissOptions) => {
  const dragControls = useDragControls();
  const offset = useMotionValue(0);
  const { axis, constraints, direction } = edges[edge];

  const handleDragEnd = (_event: unknown, gesture: PanInfo) => {
    const travelled = gesture.offset[axis] * direction;
    const flicked = gesture.velocity[axis] * direction;

    if (
      travelled > dismissDistancePx ||
      flicked > dismissFlickVelocityPxPerSecond
    ) {
      onDismiss();
    } else {
      animate(offset, 0, settleSpring);
    }
  };

  return {
    dragControls,
    offset,
    dragProps: {
      drag: enabled ? axis : (false as const),
      dragControls,
      dragConstraints: constraints,
      dragElastic: isElastic ? 0.1 : 0,
      dragDirectionLock: isDirectionLocked,
      style: axis === "x" ? { x: offset } : { y: offset },
      onDragEnd: handleDragEnd,
    },
  };
};
